import {
  ArrowLeftIcon,
  ArrowRightIcon,
  EnvelopeSimpleIcon,
  WarningCircleIcon,
} from '@phosphor-icons/react';
import { useEffect, useId, useRef, useState, type SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { formatDuration, useCountdown } from '@shared/lib/useCountdown';
import { maskEmail } from '@shared/lib/maskEmail';
import { Button } from '@shared/ui';

import { authErrorKey, isTerminalCodeError, retryAfterSeconds } from '../model/authErrors';

import styles from './AuthFlow.module.css';
import { OtpInput } from './OtpInput';

export interface OtpChallenge {
  challengeId: string;
  email: string;
  /** Момент, после которого код перестаёт действовать. */
  expiresAt: Date;
  /** Момент, начиная с которого можно запросить новый код. */
  resendAt: Date;
}

interface OtpStepProps {
  titleId: string;
  /** `h1` на странице входа, `h2` в модальном окне. */
  headingLevel?: 'h1' | 'h2' | undefined;
  challenge: OtpChallenge;
  codeLength: number;
  submitting: boolean;
  resending: boolean;
  verifyError: unknown;
  resendError: unknown;
  onVerify: (code: string) => void;
  onResend: () => void;
  onChangeEmail: () => void;
}

export function OtpStep({
  titleId,
  headingLevel = 'h2',
  challenge,
  codeLength,
  submitting,
  resending,
  verifyError,
  resendError,
  onVerify,
  onResend,
  onChangeEmail,
}: OtpStepProps) {
  // Тег заголовка выбирает вызывающий экран: см. `headingLevel`.
  const Heading = headingLevel;
  const { t } = useTranslation();
  const hintId = useId();
  const [code, setCode] = useState('');

  const expiresIn = useCountdown(challenge.expiresAt);
  const resendIn = useCountdown(challenge.resendAt);

  const expired = expiresIn === 0;
  const blocked = expired || isTerminalCodeError(verifyError);
  const error = verifyError ?? resendError;

  // Новый код — новое поле: старые цифры на экране только путают.
  const challengeId = challenge.challengeId;
  useEffect(() => {
    setCode('');
  }, [challengeId]);

  // Неверный код стирается, чтобы следующая попытка начиналась с пустого поля,
  // а не с правки чужих цифр.
  const failedAt = useRef<unknown>(null);
  useEffect(() => {
    if (verifyError && verifyError !== failedAt.current) {
      failedAt.current = verifyError;
      setCode('');
    }
  }, [verifyError]);

  const submit = (value: string) => {
    if (submitting || blocked || value.length !== codeLength) return;
    onVerify(value);
  };

  const handleSubmit = (event: SyntheticEvent) => {
    event.preventDefault();
    submit(code);
  };

  const retryAfter = retryAfterSeconds(resendError);

  return (
    <form className={styles.step} onSubmit={handleSubmit} noValidate>
      <header className={styles.header}>
        <button type="button" className={styles.back} onClick={onChangeEmail}>
          <ArrowLeftIcon size={15} weight="bold" aria-hidden="true" />
          {t('auth.otp.changeEmail')}
        </button>

        <Heading id={titleId} className={styles.title}>
          {t('auth.otp.title')}
        </Heading>

        <p className={styles.lead}>
          {t('auth.otp.lead')}{' '}
          <span className={styles.emailChip}>
            <EnvelopeSimpleIcon size={14} aria-hidden="true" />
            {maskEmail(challenge.email)}
          </span>
        </p>
      </header>

      <div className={styles.fields}>
        <OtpInput
          value={code}
          onChange={setCode}
          onComplete={submit}
          length={codeLength}
          label={t('auth.otp.label')}
          disabled={submitting || blocked}
          invalid={Boolean(verifyError)}
          describedBy={hintId}
          autoFocus
        />

        <p id={hintId} className={styles.hint}>
          {expired
            ? t('auth.otp.expiredHint')
            : t('auth.otp.validFor', { time: formatDuration(expiresIn) })}
        </p>
      </div>

      {error ? (
        <p className={styles.alert} role="alert">
          <WarningCircleIcon size={16} aria-hidden="true" className={styles.alertIcon} />
          <span>
            {t(authErrorKey(error))}
            {retryAfter ? ` ${t('auth.otp.retryAfter', { seconds: retryAfter })}` : ''}
          </span>
        </p>
      ) : null}

      {/* Кнопка проверки нужна для клавиатуры и скринридеров: набранный до конца
          код отправляется сам, но полагаться только на это нельзя. */}
      <Button
        type="submit"
        size="lg"
        fullWidth
        disabled={submitting || blocked || code.length !== codeLength}
        iconEnd={<ArrowRightIcon size={18} />}
      >
        {submitting ? t('auth.otp.submitting') : t('auth.otp.submit')}
      </Button>

      <div className={styles.resend}>
        {resendIn > 0 && !expired ? (
          <p className={styles.resendWaiting}>
            {t('auth.otp.resendIn', { time: formatDuration(resendIn) })}
          </p>
        ) : (
          <Button
            variant="ghost"
            disabled={resending}
            onClick={onResend}
            className={styles.resendButton}
          >
            {resending ? t('auth.otp.resending') : t('auth.otp.resend')}
          </Button>
        )}
      </div>
    </form>
  );
}
